import { KeyboardEvent } from 'react';
import { makeStyles, Typography } from '@material-ui/core';

import { OwnerRole, OwnerRoleChip } from '../Chips/OwnerRoleChip.component';

export type OwnerCandidate = {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: OwnerRole;
};

type Props = {
  candidates: OwnerCandidate[];
  selectedOwnerId: string;
  onSelect: (ownerId: string) => void;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
    minHeight: 0,
    maxHeight: '28rem',
    overflowY: 'auto',
    padding: '0.5rem',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    background: '#ffffff',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: '#ffffff',
    border: '2px solid transparent',
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',

    '&:hover': {
      background: '#f5f5f5',
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.admin.primary.light}`,
      outlineOffset: 2,
    },
  },
  cardSelected: {
    border: `2px solid ${theme.palette.admin.primary.main}`,
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  name: {
    fontWeight: 600,
    fontSize: 14,
  },
  email: {
    fontSize: 12,
    opacity: 0.7,
  },
}));

export function OwnerSelectionTable({ candidates, selectedOwnerId, onSelect }: Props) {
  const classes = useStyles();

  const handleKeyDown = (candidateId: string) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(candidateId);
    }
  };

  return (
    <div className={classes.container}>
      {candidates.map((candidate) => {
        const isSelected = candidate.id === selectedOwnerId;
        const cardClassName = isSelected ? `${classes.card} ${classes.cardSelected}` : classes.card;
        return (
          <div
            key={candidate.id}
            role="button"
            tabIndex={0}
            className={cardClassName}
            onClick={() => onSelect(candidate.id)}
            onKeyDown={handleKeyDown(candidate.id)}
            aria-pressed={isSelected}
          >
            <OwnerRoleChip role={candidate.role} />
            <div className={classes.identity}>
              <Typography className={classes.name}>
                {candidate.firstname} {candidate.lastname}
              </Typography>
              <Typography className={classes.email}>{candidate.email}</Typography>
            </div>
          </div>
        );
      })}
    </div>
  );
}
